import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";

// Import slice - PATH RELATIVI!
import {
    aggiornaOdPLegacy,
    fetchOdPLegacy,
    OdPLegacy,
    OdPLegacyList, seguiOdPLegacy,
    SeguiOdPLegacyResponse,
    STATI_ODP,
} from "./odpLegacySlice";

import {
    FiltriOdPLegacy,
    setFiltriOdPSeguiti,
    resetFiltriOdPSeguiti,
} from "components/userPreference/userPreferenceSlice";

import {
    fetchReparti,
    Reparto,
} from "../../anagrafiche/reparti/repartiSlice";

import {
    fetchOperatori,
    Operatore,
} from "../personale/operatoriSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";

// Componenti locali
import OdPLegacyCard from "./OdPLegacyCard";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import TextField from "@mui/material/TextField";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AddLinkIcon from "@mui/icons-material/AddLink";
import FactoryIcon from "@mui/icons-material/Factory";
import CachedIcon from '@mui/icons-material/Cached';
import {theme} from "../../theme";
import {convertToDateTime} from "../../common/dateUtils/convertToDateTime";
import {toast} from "react-toastify";
import {ClienteLight} from "../../anagrafiche/clienti/clientiSlice";
import {AutocompleteClientiAsync} from "../../anagrafiche/clienti/autocomplete/AutocompleteClientiAsync";

// ============================================
// COMPONENT
// ============================================

const OdPLegacyPage = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const odpLegacyList = useSelector<RootState, OdPLegacyList>(
        (state) => state.odpLegacyState.odpLegacyList
    );

    const filtri = useSelector<RootState, FiltriOdPLegacy>(
        (state) => state.userPreferenceState.filtriOdPSeguiti || {}
    );

    const reparti = useSelector<RootState, Reparto[]>(
        (state) => state.repartiState?.repartiList?.results || []
    );

    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [searchValue, setSearchValue] = useState("");
    const [refresh, setRefresh] = useState(false);
    const [inAggiornamento, setInAggiornamento] = useState(false);

    // Filtri locali UI
    const [statiSelezionati, setStatiSelezionati] = useState<number[]>(
        filtri?.stati || []
    );

    const [clienteSelezionato, setClienteSelezionato] = useState<ClienteLight | null>(
        filtri?.cliente || null
    );

    const [repartiSelezionati, setRepartiSelezionati] = useState<number[]>(
        filtri?.reparto || []
    );

    // Operatori selezionati per codice: mappa direttamente filtri.operatori (string[])
    const [operatoriSelezionati, setOperatoriSelezionati] = useState<string[]>(
        filtri?.operatori || []
    );

    // Form "Segui nuovi OdP"
    const [esercizio, setEsercizio] = useState<string>(convertToDateTime(new Date())?.toFormat('yyyy')!);
    const [tipoDocumento, setTipoDocumento] =  useState<string>("1");
    const [numeroRegistrazione, setNumeroRegistrazione] = useState<string>("");

    // Menu anchors
    const [anchorElStatoFilter, setAnchorElStatoFilter] =
        useState<null | HTMLElement>(null);
    const [anchorElRepartoFilter, setAnchorElRepartoFilter] =
        useState<null | HTMLElement>(null);
    const [anchorElOperatoreFilter, setAnchorElOperatoreFilter] =
        useState<null | HTMLElement>(null);

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        dispatch(fetchReparti());
        dispatch(fetchOperatori());
    }, []);

    useEffect(() => {
        const nuoviFiltri: FiltriOdPLegacy = {
            ...filtri,
            search: searchValue || undefined,
            stati: statiSelezionati.length > 0 ? statiSelezionati : undefined,
            cliente: clienteSelezionato,
            reparto: repartiSelezionati.length > 0 ? repartiSelezionati : undefined,
            operatori: operatoriSelezionati.length > 0 ? operatoriSelezionati : undefined,
            page: 1,
        };

        dispatch(setFiltriOdPSeguiti(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
        setRefresh(false);
    }, [refresh]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleResetFiltri = () => {
        setSearchValue("");
        setStatiSelezionati([]);
        setRepartiSelezionati([]);
        setOperatoriSelezionati([]);

        dispatch(resetFiltriOdPSeguiti());

        const filtriDefault: FiltriOdPLegacy = {
            numero_record: 20,
            page: 1,
        };
        dispatch(setFiltriOdPSeguiti(filtriDefault));
        dispatch(fetchOdPLegacy(filtriDefault));
    };

    const handlePaginationChange = (
        event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        const nuoviFiltri = {...filtri, page};
        dispatch(setFiltriOdPSeguiti(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        const numero_record = event.target.value as number;
        const nuoviFiltri: FiltriOdPLegacy = {
            ...filtri,
            numero_record,
            page: 1,
        };
        dispatch(setFiltriOdPSeguiti(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
    };

    const toggleStato = (stato: number) => {
        setStatiSelezionati((prev) =>
            prev.includes(stato)
                ? prev.filter((s) => s !== stato)
                : [...prev, stato]
        );
    };

    const toggleReparto = (repartoId: number) => {
        setRepartiSelezionati((prev) =>
            prev.includes(repartoId)
                ? prev.filter((r) => r !== repartoId)
                : [...prev, repartoId]
        );
    };

    const toggleOperatore = (codice: string) => {
        setOperatoriSelezionati((prev) =>
            prev.includes(codice)
                ? prev.filter((c) => c !== codice)
                : [...prev, codice]
        );
    };

    const handleSeguiOdP = async () => {
        const numero_registrazione = parseInt(numeroRegistrazione, 10);
        if (isNaN(numero_registrazione) || numero_registrazione <= 0) {
            return;
        }

        const tipo_documento = parseInt(tipoDocumento, 10);
        if (isNaN(tipo_documento) || tipo_documento <= 0) {
            return;
        }

        const numero_esercizio = parseInt(esercizio, 10);
        if (isNaN(numero_esercizio) || numero_esercizio <= 0) {
            return;
        }
        const risposta = await dispatch(seguiOdPLegacy({esercizio: numero_esercizio, tipo_documento: tipo_documento, numero_registrazione:numero_registrazione,}));
        if (risposta.meta.requestStatus === "fulfilled") {
            const { odp, errori } = risposta.payload as SeguiOdPLegacyResponse;
            const articoli = odp.map((o) => o.codice_articolo).filter(Boolean).join(", ");
            if (odp.length === 0 && errori.length > 0) {
                toast.error(`Nessun OdP agganciato. ${errori[0]}`);
            } else if (errori.length > 0) {
                toast.warning(`${odp.length} OdP agganciati (${articoli}); ${errori.length} errori: ${errori[0]}`);
            } else if (odp.length === 1) {
                toast.success(`OdP ${articoli} agganciato`);
            } else {
                toast.success(`${odp.length} OdP agganciati: ${articoli}`);
            }
        }

        setNumeroRegistrazione("");
    };

    const handleAggiornaOdP = async () => {
        setInAggiornamento(true)
        const risposta = await dispatch(aggiornaOdPLegacy());
        if (risposta.meta.requestStatus === "fulfilled") {
            toast.success('OdP aggiornati')
            setRefresh(true);
            setInAggiornamento(false)
        }

        setNumeroRegistrazione("");
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{p: 3}}>
            {/* Header */}
            <Grid container spacing={2} sx={{mb: 1}}>
                <Grid size={{xs: 12, sm: 6}}>
                    <Typography variant="h5" fontWeight="bold">
                        OdP Legacy
                    </Typography>
                </Grid>

                <Grid size={{xs: 12, sm: 6}}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{justifyContent: "flex-end"}}
                    >
                        {/* Ricerca con OutlinedInput e onBlur */}
                        <FormControl
                            sx={{m: 1, minWidth: 250, width: "70%"}}
                            variant="outlined"
                            size="small"
                        >
                            <InputLabel>Ricerca</InputLabel>
                            <OutlinedInput
                                type="text"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setRefresh(true)}
                                            edge="end"
                                            size="small"
                                        >
                                            <SearchIcon/>
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Ricerca"
                                placeholder="Cliente, articolo..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onBlur={() => setRefresh(true)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setRefresh(true);
                                    }
                                }}
                            />
                        </FormControl>
                    </Stack>
                </Grid>
            </Grid>

            {/* Form "Segui nuovi OdP" */}
            <Card
                sx={{
                    mb: theme.spacing(2),
                    p: theme.spacing(2),
                    backgroundColor: theme.palette.primary.light + "10",
                    border: `1px solid ${theme.palette.primary.light}`,
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{alignItems: "center"}}
                    >
                        <AddLinkIcon color="primary"/>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Segui nuovo Ordine di Produzione
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            label="Esercizio"
                            value={esercizio}
                            onChange={(e) => setEsercizio(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleSeguiOdP();
                                }
                            }}
                            sx={{width: 180}}
                            slotProps={{
                                htmlInput: {min: 1},
                            }}
                        />
                        <Typography>/</Typography>
                        <TextField
                            size="small"
                            type="number"
                            label="Tipo Documento"
                            value={tipoDocumento}
                            onChange={(e) => setTipoDocumento(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleSeguiOdP();
                                }
                            }}
                            sx={{width: 180}}
                            slotProps={{
                                htmlInput: {min: 1},
                            }}
                        />
                        <Typography>/</Typography>
                        <TextField
                            size="small"
                            type="number"
                            label="Numero registrazione"
                            value={numeroRegistrazione}
                            onChange={(e) => setNumeroRegistrazione(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleSeguiOdP();
                                }
                            }}
                            sx={{width: 180}}
                            slotProps={{
                                htmlInput: {min: 1},
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSeguiOdP}
                            disabled={!numeroRegistrazione || !tipoDocumento}
                            startIcon={<AddLinkIcon/>}
                        >
                            Segui
                        </Button>
                    </Stack>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleAggiornaOdP}
                        startIcon={<CachedIcon/>}
                    >
                        Aggiorna Seguiti
                    </Button>
                </Stack>
            </Card>

            {/* Filtri */}
            <Card
                sx={{
                    mb: theme.spacing(2),
                    p: theme.spacing(2),
                }}
            >
                <Grid container spacing={2} sx={{alignItems: "center", flexWrap: "nowrap"}}>
                    {/* Filtro stati - chip con menu */}
                    <Grid size="auto">
                        <Stack direction="row" spacing={1} sx={{alignItems: "center", flexWrap: "wrap", gap: 1}}>
                            <Typography variant="body2" fontWeight="bold">
                                Stato:
                            </Typography>
                            {statiSelezionati.length > 2 ? (
                                <Chip
                                    label={`${statiSelezionati.length} stati`}
                                    color="primary"
                                    size="small"
                                    onDelete={() => {
                                        setStatiSelezionati([]);
                                        setRefresh(true);
                                    }}
                                />
                            ) : statiSelezionati.length > 0 ? (
                                statiSelezionati.map((stato) => (
                                    <Chip
                                        key={stato}
                                        label={STATI_ODP[stato]}
                                        color={getStatoChipColor(stato)}
                                        size="small"
                                        onDelete={() => {
                                            toggleStato(stato);
                                            setRefresh(true);
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Tutti
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={(e) =>
                                    setAnchorElStatoFilter(e.currentTarget)
                                }
                                sx={{
                                    color:
                                        statiSelezionati.length > 0
                                            ? "primary.main"
                                            : "inherit",
                                }}
                            >
                                <FilterAltIcon fontSize="small"/>
                            </IconButton>
                        </Stack>
                    </Grid>

                    {/* Filtro reparti - chip con menu */}
                    <Grid size="auto">
                        <Stack direction="row" spacing={1} sx={{alignItems: "center", flexWrap: "wrap", gap: 1}}>
                            <Typography variant="body2" fontWeight="bold">
                                Reparto:
                            </Typography>
                            {repartiSelezionati.length > 2 ? (
                                <Chip
                                    label={`${repartiSelezionati.length} reparti`}
                                    color="primary"
                                    size="small"
                                    onDelete={() => {
                                        setRepartiSelezionati([]);
                                        setRefresh(true);
                                    }}
                                />
                            ) : repartiSelezionati.length > 0 ? (
                                repartiSelezionati.map((repartoId) => {
                                    const reparto = reparti.find((r) => r.id === repartoId);
                                    return (
                                        <Chip
                                            key={repartoId}
                                            label={reparto ? (reparto.descrizione ? `${reparto.codice} - ${reparto.descrizione}` : reparto.codice) : repartoId}
                                            color="primary"
                                            size="small"
                                            onDelete={() => {
                                                toggleReparto(repartoId);
                                                setRefresh(true);
                                            }}
                                        />
                                    );
                                })
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Tutti
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={(e) =>
                                    setAnchorElRepartoFilter(e.currentTarget)
                                }
                                sx={{
                                    color:
                                        repartiSelezionati.length > 0
                                            ? "primary.main"
                                            : "inherit",
                                }}
                            >
                                <FilterAltIcon fontSize="small"/>
                            </IconButton>
                        </Stack>
                    </Grid>

                    {/* Filtro operatori - chip con menu */}
                    <Grid size="auto">
                        <Stack direction="row" spacing={1} sx={{alignItems: "center", flexWrap: "wrap", gap: 1}}>
                            <Typography variant="body2" fontWeight="bold">
                                Operatore:
                            </Typography>
                            {operatoriSelezionati.length > 2 ? (
                                <Chip
                                    label={`${operatoriSelezionati.length} operatori`}
                                    color="primary"
                                    size="small"
                                    onDelete={() => {
                                        setOperatoriSelezionati([]);
                                        setRefresh(true);
                                    }}
                                />
                            ) : operatoriSelezionati.length > 0 ? (
                                operatoriSelezionati.map((codice) => {
                                    const operatore = operatori.find((op) => op.codice === codice);
                                    return (
                                        <Chip
                                            key={codice}
                                            label={operatore ? (operatore.nominativo ? `${operatore.codice} - ${operatore.nominativo}` : operatore.codice) : codice}
                                            color="primary"
                                            size="small"
                                            onDelete={() => {
                                                toggleOperatore(codice);
                                                setRefresh(true);
                                            }}
                                        />
                                    );
                                })
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Tutti
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={(e) =>
                                    setAnchorElOperatoreFilter(e.currentTarget)
                                }
                                sx={{
                                    color:
                                        operatoriSelezionati.length > 0
                                            ? "primary.main"
                                            : "inherit",
                                }}
                            >
                                <FilterAltIcon fontSize="small"/>
                            </IconButton>
                        </Stack>
                    </Grid>
                    {/* Filtro Clienti */}

                    <AutocompleteClientiAsync
                        label="Filtra per cliente"
                        value={clienteSelezionato}
                        onChange={(newValue) => {
                            setClienteSelezionato(newValue);
                            // Trigger refresh immediato quando cambia il cliente
                            setRefresh(true);
                        }}
                        disabled={false}
                        minSearchLength={2}
                        debounceDelay={300}
                        sx={{minWidth:300}}
                    />
                    {/* Filtro esercizio */}
                    <Grid size="auto">
                        <TextField
                            size="small"
                            label="Esercizio"
                            value={filtri.esercizio || ""}
                            onChange={(e) => {
                                dispatch(
                                    setFiltriOdPSeguiti({
                                        ...filtri,
                                        esercizio: e.target.value ? [Number(e.target.value)] : undefined,
                                    })
                                );
                            }}
                            onBlur={() => setRefresh(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setRefresh(true);
                                }
                            }}
                            fullWidth
                            sx={{
                                backgroundColor: filtri.esercizio
                                    ? theme.palette.action.selected
                                    : "inherit",
                            }}
                        />
                    </Grid>

                    {/* Filtro numero registrazione */}
                    <Grid size="auto">
                        <TextField
                            size="small"
                            label="N. Registrazione"
                            value={filtri.numero_registrazione || ""}
                            onChange={(e) => {
                                dispatch(
                                    setFiltriOdPSeguiti({
                                        ...filtri,
                                        numero_registrazione: e.target.value ? [e.target.value] : undefined,
                                    })
                                );
                            }}
                            onBlur={() => setRefresh(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setRefresh(true);
                                }
                            }}
                            sx={{
                                backgroundColor: filtri.numero_registrazione
                                    ? theme.palette.action.selected
                                    : "inherit",
                                minWidth: 140,
                            }}
                        />
                    </Grid>

                    {/* Filtro codice articolo */}
                    <Grid size="auto">
                        <TextField
                            size="small"
                            label="Codice Articolo"
                            value={filtri.codice_articolo || ""}
                            onChange={(e) => {
                                dispatch(
                                    setFiltriOdPSeguiti({
                                        ...filtri,
                                        codice_articolo:
                                            e.target.value ? [e.target.value] : undefined,
                                    })
                                );
                            }}
                            onBlur={() => setRefresh(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setRefresh(true);
                                }
                            }}
                            fullWidth
                            sx={{
                                backgroundColor: filtri.codice_articolo
                                    ? theme.palette.action.selected
                                    : "inherit",
                            }}
                        />
                    </Grid>

                    {/* Reset filtri */}
                    <Grid size="auto" sx={{ml: "auto"}}>
                        <IconButton onClick={handleResetFiltri} color="primary">
                            <FilterAltOffIcon/>
                        </IconButton>
                    </Grid>
                </Grid>
            </Card>

            {/* Lista OdP Cards */}
            <Box>
                {odpLegacyList.results.map((odp: OdPLegacy) => (
                    <OdPLegacyCard key={odp.id} odp={odp} filtri={filtri}/>
                ))}
                {/* Empty state */}
                {odpLegacyList.results.length === 0 &&
                    odpLegacyList.errorsStack.status !==
                    ErrorStatusTypes.PENDING && (
                        <Card>
                            <CardContent>
                                <Box sx={{textAlign: "center", py: 4}}>
                                    <FactoryIcon
                                        sx={{
                                            fontSize: 64,
                                            color: "grey.400",
                                            mb: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                    >
                                        Nessun OdP Legacy trovato
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{mt: 1}}
                                    >
                                        Usa il form sopra per iniziare a seguire
                                        un Ordine di Produzione
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                {/* Paginazione */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                    }}
                >
                    <FormControl sx={{minWidth: 120}} size="small">
                        <InputLabel>Record per pagina</InputLabel>
                        <Select
                            value={filtri.numero_record || 20}
                            label="Record per pagina"
                            onChange={handleRecordsPerPageChange}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>

                    <Pagination
                        count={odpLegacyList.num_pages}
                        page={filtri.page || 1}
                        onChange={handlePaginationChange}
                        color="primary"
                    />

                    <Typography variant="body2" color="text.secondary">
                        Totale: {odpLegacyList.count} record
                    </Typography>
                </Box>


            </Box>

            {/* Menu filtro stati */}
            <Menu
                anchorEl={anchorElStatoFilter}
                open={Boolean(anchorElStatoFilter)}
                onClose={() => setAnchorElStatoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {Object.entries(STATI_ODP).map(([key, value]) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={statiSelezionati.includes(
                                        Number(key)
                                    )}
                                    onChange={() => {
                                        toggleStato(Number(key));
                                        setRefresh(true);
                                    }}
                                />
                            }
                            label={value}
                        />
                    ))}
                </FormGroup>
            </Menu>

            {/* Menu filtro reparti */}
            <Menu
                anchorEl={anchorElRepartoFilter}
                open={Boolean(anchorElRepartoFilter)}
                onClose={() => setAnchorElRepartoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {reparti.filter((r) => r.attivo).map((reparto) => (
                        <FormControlLabel
                            key={reparto.id}
                            control={
                                <Checkbox
                                    checked={repartiSelezionati.includes(
                                        reparto.id || 0
                                    )}
                                    onChange={() => {
                                        toggleReparto(reparto.id || 0);
                                        setRefresh(true);
                                    }}
                                />
                            }
                            label={reparto.descrizione ? `${reparto.codice} - ${reparto.descrizione}` : reparto.codice || ""}
                        />
                    ))}
                </FormGroup>
            </Menu>

            {/* Menu filtro operatori */}
            <Menu
                anchorEl={anchorElOperatoreFilter}
                open={Boolean(anchorElOperatoreFilter)}
                onClose={() => setAnchorElOperatoreFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {operatori.filter((op) => op.attivo).map((operatore) => (
                        <FormControlLabel
                            key={operatore.id}
                            control={
                                <Checkbox
                                    checked={operatoriSelezionati.includes(
                                        operatore.codice || ""
                                    )}
                                    onChange={() => {
                                        toggleOperatore(operatore.codice || "");
                                        setRefresh(true);
                                    }}
                                />
                            }
                            label={operatore.nominativo ? `${operatore.codice} - ${operatore.nominativo}` : operatore.codice || ""}
                        />
                    ))}
                </FormGroup>
            </Menu>

            {/* Loading */}
            <Backdrop
                open={ inAggiornamento ||
                    odpLegacyList.errorsStack.status ===
                    ErrorStatusTypes.PENDING
                }
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
};

// ============================================
// HELPER
// ============================================

const getStatoChipColor = (
    stato: number
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (stato) {
        case 0:
            return "default";
        case 1:
            return "info";
        case 2:
            return "primary";
        case 3:
            return "warning";
        case 4:
            return "success";
        default:
            return "default";
    }
};

export default OdPLegacyPage;